/**
 * Project Collaborators page.
 * Manage collaborators for a specific project.
 */

import { useState, useMemo, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useProject } from "../hooks/useProjects";
import { useWorkspace } from "../contexts/WorkspaceContext";
import { useWorkspaceUrl } from "../lib/routes";
import { useWorkspaceCollaborators } from "../hooks/useWorkspaceMembers";
import { PageHeader } from "../components/layout/PageHeader";
import {
  getProjectCollaborators,
  addProjectCollaborator,
  updateProjectCollaborator,
  removeProjectCollaborator,
  type Collaborator,
  type CollaboratorsResponse,
  type WorkspaceCollaborator,
} from "../services/naraDoor";
import {
  Users,
  Plus,
  UserMinus,
  Mail,
  Phone,
  X,
} from "lucide-react";
import { InviteForm } from "../components/invite/InviteForm";

export function ProjectCollaborators() {
  const { t } = useTranslation();
  const { id: projectId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { activeWorkspace } = useWorkspace();
  const urls = useWorkspaceUrl();
  // useProject now gets workspaceId automatically from the store
  const { data: project, isLoading: projectLoading } = useProject(projectId);

  const [showAddDialog, setShowAddDialog] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Workspace member search state
  const [selectedMember, setSelectedMember] = useState<WorkspaceCollaborator | null>(null);
  const [memberSearchQuery, setMemberSearchQuery] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Fetch project collaborators
  const workspaceId = activeWorkspace?.id;
  const collaboratorsKey = ["projectCollaborators", workspaceId, projectId];
  const {
    data: collaboratorsData,
    isLoading: isLoadingCollaborators,
    isError: collaboratorsFailed,
  } = useQuery<CollaboratorsResponse>({
    queryKey: collaboratorsKey,
    queryFn: () => getProjectCollaborators(workspaceId!, projectId!),
    enabled: !!workspaceId && !!projectId,
  });

  const collaborators = collaboratorsData?.collaborators || [];

  // Fetch workspace members for autocomplete
  const { data: workspaceMembersData } = useWorkspaceCollaborators();
  const workspaceMembers = workspaceMembersData?.collaborators || [];

  // Filter suggestions: match search query, exclude members already on the project
  const availableSuggestions = useMemo(() => {
    if (!memberSearchQuery.trim()) return [];
    const query = memberSearchQuery.toLowerCase();
    const projectUserIds = new Set(collaborators.map((c) => c.user_id));
    return workspaceMembers
      .filter((m) => {
        if (projectUserIds.has(m.user_id)) return false;
        const fn = m.user?.first_name?.toLowerCase() || "";
        const ln = m.user?.last_name?.toLowerCase() || "";
        const email = m.user?.email?.toLowerCase() || "";
        const ph = m.user?.phone || "";
        return fn.includes(query) || ln.includes(query) || email.includes(query) || ph.includes(query) || `${fn} ${ln}`.includes(query);
      })
      .slice(0, 8);
  }, [memberSearchQuery, workspaceMembers, collaborators]);

  // Close suggestions dropdown on click outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelectMember = (member: WorkspaceCollaborator) => {
    setSelectedMember(member);
    setMemberSearchQuery("");
    setShowSuggestions(false);
  };

  const handleClearSelection = () => {
    setSelectedMember(null);
    setMemberSearchQuery("");
  };

  const closeDialog = () => {
    setShowAddDialog(false);
    setFormError(null);
    setSelectedMember(null);
    setMemberSearchQuery("");
    setShowSuggestions(false);
  };

  // Add an existing workspace member to the project (someone new goes through InviteForm)
  const addCollaboratorMutation = useMutation({
    mutationFn: (member: WorkspaceCollaborator) => {
      return addProjectCollaborator(workspaceId!, projectId!, { userId: member.user_id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: collaboratorsKey });
      closeDialog();
    },
    onError: (error: Error) => {
      console.error("addProjectCollaborator failed:", error.message);
      setFormError(error.message);
    },
  });

  // Remove collaborator mutation with optimistic update
  const removeCollaboratorMutation = useMutation({
    mutationFn: (userId: string) => removeProjectCollaborator(workspaceId!, projectId!, userId),
    onMutate: async (userId: string) => {
      // Cancel outgoing refetches to avoid race conditions
      await queryClient.cancelQueries({ queryKey: collaboratorsKey });

      // Snapshot the previous value
      const previousCollaborators = queryClient.getQueryData<CollaboratorsResponse>(collaboratorsKey);

      // Optimistically update the cache - remove the collaborator immediately
      queryClient.setQueryData<CollaboratorsResponse>(
        collaboratorsKey,
        (old) => {
          if (!old) return old;
          return {
            ...old,
            collaborators: old.collaborators.filter((c) => c.user_id !== userId),
          };
        }
      );

      // Return context with the previous value for rollback
      return { previousCollaborators };
    },
    onError: (err, userId, context) => {
      console.error("removeProjectCollaborator failed", { workspaceId, projectId, userId, error: err.message });
      // Rollback on error
      if (context?.previousCollaborators) {
        queryClient.setQueryData(collaboratorsKey, context.previousCollaborators);
      }
    },
    onSettled: () => {
      // Always refetch after error or success to ensure consistency
      queryClient.invalidateQueries({ queryKey: collaboratorsKey });
    },
  });

  // Update collaborator mutation
  const updateCollaboratorMutation = useMutation({
    mutationFn: ({ userId, emailTranscription }: { userId: string; emailTranscription: boolean }) => {
      return updateProjectCollaborator(workspaceId!, projectId!, userId, { emailTranscription });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: collaboratorsKey });
    },
    onError: (err, { userId }) => {
      console.error("updateProjectCollaborator failed", { workspaceId, projectId, userId, error: err.message });
    },
  });

  // Helper to get collaborator name - handles both nested and flat data structures
  const getCollaboratorName = (collab: Collaborator) => {
    // Try nested user object first, then flat structure
    const firstName = collab.user?.first_name || (collab as unknown as { first_name?: string }).first_name;
    const lastName = collab.user?.last_name || (collab as unknown as { last_name?: string }).last_name;
    const parts = [firstName, lastName].filter(Boolean);
    if (parts.length > 0) return parts.join(" ");

    const email = collab.user?.email || (collab as unknown as { email?: string }).email;
    return email || "Unknown";
  };

  // Helper to get collaborator phone - handles both nested and flat data structures
  const getCollaboratorPhone = (collab: Collaborator) => {
    return collab.user?.phone || (collab as unknown as { phone?: string }).phone;
  };

  // Helper to get collaborator email - handles both nested and flat data structures
  const getCollaboratorEmail = (collab: Collaborator) => {
    return collab.user?.email || (collab as unknown as { email?: string }).email;
  };

  if (!projectId) {
    return null;
  }

  if (projectLoading || isLoadingCollaborators) {
    return (
      <div className="space-y-6">
        {/* Breadcrumb skeleton */}
        <div className="flex items-center gap-2">
          <div className="h-4 w-16 bg-muted rounded animate-pulse" />
          <div className="h-4 w-4 bg-muted rounded animate-pulse" />
          <div className="h-4 w-24 bg-muted rounded animate-pulse" />
          <div className="h-4 w-4 bg-muted rounded animate-pulse" />
          <div className="h-4 w-28 bg-muted rounded animate-pulse" />
        </div>
        {/* Title + description skeleton */}
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <div className="h-8 w-48 bg-muted rounded animate-pulse" />
            <div className="h-4 w-64 bg-muted rounded animate-pulse" />
          </div>
          <div className="h-10 w-40 bg-muted rounded-lg animate-pulse" />
        </div>
        {/* Table skeleton */}
        <div className="bg-card rounded-lg border border-border overflow-hidden">
          <div className="bg-muted border-b border-border px-4 py-3 flex gap-4">
            <div className="h-4 w-24 bg-muted-foreground/10 rounded animate-pulse" />
            <div className="h-4 w-32 bg-muted-foreground/10 rounded animate-pulse" />
            <div className="h-4 w-20 bg-muted-foreground/10 rounded animate-pulse" />
            <div className="h-4 w-20 bg-muted-foreground/10 rounded animate-pulse" />
          </div>
          {[1, 2, 3].map((i) => (
            <div key={i} className="px-4 py-4 border-b border-border flex items-center gap-4">
              <div className="h-5 w-32 bg-muted rounded animate-pulse" />
              <div className="h-5 w-48 bg-muted rounded animate-pulse" />
              <div className="h-5 w-16 bg-muted rounded animate-pulse" />
              <div className="h-5 w-16 bg-muted rounded animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={t("projectCollaborators.title")}
        description={project ? t("projectCollaborators.description", { projectName: project.name }) : undefined}
        breadcrumbs={[
          { label: t("nav.projects"), href: urls?.projects },
          { label: project?.name || "Project", href: urls && projectId ? urls.project(projectId) : undefined },
          { label: t("projectCollaborators.title") },
        ]}
        actions={
          <button
            onClick={() => setShowAddDialog(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            {t("projectCollaborators.addCollaborator")}
          </button>
        }
      />

      {removeCollaboratorMutation.isError && (
        <p role="alert" className="mb-4 text-sm text-destructive">{t("projectCollaborators.errors.removeFailed")}</p>
      )}
      {updateCollaboratorMutation.isError && (
        <p role="alert" className="mb-4 text-sm text-destructive">{t("projectCollaborators.errors.updateFailed")}</p>
      )}

      {/* Collaborators List */}
      {isLoadingCollaborators ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 bg-muted rounded-lg animate-pulse" />
          ))}
        </div>
      ) : collaboratorsFailed ? (
        <p role="alert" className="text-sm text-destructive">{t("projectCollaborators.errors.loadFailed")}</p>
      ) : collaborators.length > 0 ? (
        <div className="bg-card rounded-lg border border-border overflow-hidden">
          {/* Mobile: Card list */}
          <div className="sm:hidden divide-y divide-border">
            {collaborators.map((collab) => {
              const userId = collab.user_id;
              const phone = getCollaboratorPhone(collab);
              const email = getCollaboratorEmail(collab);
              return (
                <div key={userId} className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-foreground">
                        {getCollaboratorName(collab)}
                      </div>
                      <div className="flex flex-col gap-0.5 mt-1">
                        <span className="flex items-center gap-1 text-sm text-muted-foreground">
                          <Mail className="h-3 w-3 text-muted-foreground" />
                          {email || "—"}
                        </span>
                        <span className="flex items-center gap-1 text-sm text-muted-foreground">
                          <Phone className="h-3 w-3 text-muted-foreground" />
                          {phone || "—"}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => removeCollaboratorMutation.mutate(userId)}
                      disabled={removeCollaboratorMutation.isPending}
                      className="p-2 text-muted-foreground hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950 rounded-lg transition-colors"
                      title={t("projectCollaborators.removeFromProject")}
                    >
                      <UserMinus className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Desktop: Table */}
          <table className="w-full hidden sm:table">
            <thead className="bg-muted border-b border-border">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium text-foreground">
                  {t("projectCollaborators.tableHeader.name")}
                </th>
                <th className="text-left px-4 py-3 text-sm font-medium text-foreground">
                  {t("projectCollaborators.tableHeader.contact")}
                </th>
                <th className="text-center px-4 py-3 text-sm font-medium text-foreground">
                  {t("projectCollaborators.tableHeader.emailReport")}
                </th>
                <th className="text-center px-4 py-3 text-sm font-medium text-foreground w-24">
                  {t("projectCollaborators.tableHeader.remove")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {collaborators.map((collab) => {
                const userId = collab.user_id;
                const phone = getCollaboratorPhone(collab);
                const email = getCollaboratorEmail(collab);
                  return (
                  <tr key={userId}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-foreground">
                        {getCollaboratorName(collab)}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col">
                        <span className="flex items-center gap-1 text-sm text-foreground">
                          <Mail className="h-4 w-4 text-muted-foreground" />
                          {email || "—"}
                        </span>
                        <span className="flex items-center gap-1 text-sm text-muted-foreground mt-0.5">
                          <Phone className="h-4 w-4 text-muted-foreground" />
                          {phone || "—"}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={collab.email_transcription ?? false}
                          onChange={(e) => {
                            updateCollaboratorMutation.mutate({
                              userId,
                              emailTranscription: e.target.checked,
                            });
                          }}
                          disabled={updateCollaboratorMutation.isPending}
                          className="sr-only peer"
                        />
                        <div className="w-9 h-5 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-border dark:after:border-gray-600 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center">
                        <button
                          onClick={() => removeCollaboratorMutation.mutate(userId)}
                          disabled={removeCollaboratorMutation.isPending}
                          className="p-2 text-muted-foreground hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950 rounded-lg transition-colors"
                          title={t("projectCollaborators.removeFromProject")}
                        >
                          <UserMinus className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-card rounded-lg border border-border p-8 text-center">
          <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="font-medium text-foreground mb-2">{t("projectCollaborators.emptyTitle")}</h3>
          <p className="text-muted-foreground mb-4">
            {t("projectCollaborators.emptyDescription")}
          </p>
          <button
            onClick={() => setShowAddDialog(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            {t("projectCollaborators.addCollaborator")}
          </button>
        </div>
      )}

      {/* Invite / add collaborator dialog */}
      {showAddDialog && activeWorkspace && projectId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-6">
              <h2 className="text-lg font-semibold text-foreground mb-4">
                {t("projectCollaborators.dialogTitle")}
              </h2>
              <div>
                {formError && (
                  <div className="mb-4 p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-600">
                    {formError}
                  </div>
                )}
                <div className="space-y-4">
                  {/* Workspace member search */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      {t("projectCollaborators.searchWorkspaceMembers")}
                    </label>
                    {selectedMember ? (
                      <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg">
                        <span className="flex-1 text-sm text-foreground">
                          {selectedMember.user?.first_name} {selectedMember.user?.last_name}
                          {selectedMember.user?.email && (
                            <span className="text-muted-foreground ml-1">({selectedMember.user.email})</span>
                          )}
                        </span>
                        <button
                          type="button"
                          onClick={handleClearSelection}
                          className="p-0.5 text-muted-foreground hover:text-foreground rounded transition-colors"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="relative" ref={suggestionsRef}>
                        <input
                          type="text"
                          value={memberSearchQuery}
                          onChange={(e) => {
                            setMemberSearchQuery(e.target.value);
                            setShowSuggestions(true);
                          }}
                          onFocus={() => memberSearchQuery.trim() && setShowSuggestions(true)}
                          placeholder={t("projectCollaborators.searchPlaceholder")}
                          className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        {showSuggestions && memberSearchQuery.trim() && (
                          <div className="absolute z-10 mt-1 w-full bg-card border border-border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                            {availableSuggestions.length > 0 ? (
                              availableSuggestions.map((member) => (
                                <button
                                  key={member.user_id}
                                  type="button"
                                  onClick={() => handleSelectMember(member)}
                                  className="w-full text-left px-3 py-2 hover:bg-accent transition-colors first:rounded-t-lg last:rounded-b-lg"
                                >
                                  <div className="text-sm font-medium text-foreground">
                                    {member.user?.first_name} {member.user?.last_name}
                                  </div>
                                  <div className="text-xs text-muted-foreground">
                                    {[member.user?.email, member.user?.phone].filter(Boolean).join(" · ")}
                                  </div>
                                </button>
                              ))
                            ) : (
                              <div className="px-3 py-2 text-sm text-muted-foreground">
                                {t("projectCollaborators.noResults", { defaultValue: "No matching members" })}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                    <p className="mt-1 text-xs text-muted-foreground">
                      {t("projectCollaborators.searchHint")}
                    </p>
                  </div>

                  {selectedMember ? (
                    <div className="flex justify-end gap-3">
                      <button
                        type="button"
                        onClick={closeDialog}
                        className="px-4 py-2 text-foreground hover:bg-accent rounded-lg transition-colors"
                      >
                        {t("common.cancel")}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setFormError(null);
                          addCollaboratorMutation.mutate(selectedMember);
                        }}
                        disabled={addCollaboratorMutation.isPending}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {addCollaboratorMutation.isPending ? t("projectCollaborators.adding") : t("projectCollaborators.addToProject")}
                      </button>
                    </div>
                  ) : (
                    <>
                      {/* Divider */}
                      <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                          <div className="w-full border-t border-border" />
                        </div>
                        <div className="relative flex justify-center text-xs">
                          <span className="bg-card px-2 text-muted-foreground">
                            {t("projectCollaborators.orInviteSomeoneNew")}
                          </span>
                        </div>
                      </div>
                      <InviteForm
                        workspaceId={activeWorkspace.id}
                        projectId={projectId}
                        onCancel={closeDialog}
                        onDone={closeDialog}
                      />
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
